/* eslint-disable */
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Divider,
  Autocomplete,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  createTheme,
  ThemeProvider,
  LinearProgress,
  Tooltip,
  Collapse,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { fetcher, postFetcher } from 'src/utils/axios'
import Iconify from 'src/components/iconify'
import { useInventory } from 'src/context/inventory-create-provider'

// ── Theme ──────────────────────────────────────────────────────────────────────
const dialogTheme = createTheme({
  palette: {
    primary: { main: '#5B5FEF', light: '#ECEEFF', dark: '#3B3FBF', contrastText: '#fff' },
    success: { main: '#16A34A', light: '#DCFCE7' },
    warning: { main: '#D97706', light: '#FEF3C7' },
    error: { main: '#DC2626', light: '#FEE2E2' },
    grey: { 50: '#F9FAFB', 100: '#F3F4F6', 200: '#E5E7EB', 300: '#D1D5DB', 500: '#6B7280', 600: '#4B5563', 800: '#1F2937', 900: '#111827' },
    background: { default: '#FFFFFF', paper: '#FFFFFF' },
    text: { primary: '#111827', secondary: '#6B7280' },
    divider: '#E5E7EB',
  },
  typography: {
    fontFamily: '"Geist", "DM Sans", "Helvetica Neue", Arial, sans-serif',
    h6: { fontWeight: 700, letterSpacing: '-0.4px' },
    subtitle2: { fontWeight: 600, letterSpacing: '-0.1px' },
    body2: { fontSize: '0.825rem' },
    caption: { fontSize: '0.75rem' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 32px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { padding: '0' } },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: '0' } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '0' } },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#FAFAFA',
            fontSize: '0.875rem',
            transition: 'all 0.15s ease',
            '& fieldset': { borderColor: '#E5E7EB' },
            '&:hover': { backgroundColor: '#F9FAFB', '& fieldset': { borderColor: '#C7C9FF' } },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
              boxShadow: '0 0 0 3px rgba(91,95,239,0.12)',
              '& fieldset': { borderColor: '#5B5FEF', borderWidth: 1.5 },
            },
          },
          '& .MuiInputLabel-root': { fontSize: '0.875rem', color: '#6B7280' },
          '& .MuiInputLabel-root.Mui-focused': { color: '#5B5FEF' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: '-0.1px',
          padding: '9px 18px',
          boxShadow: 'none',
          transition: 'all 0.15s ease',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #6366F1 0%, #5B5FEF 100%)',
          '&:hover': { background: 'linear-gradient(135deg, #4F52D3 0%, #4648C8 100%)', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(91,95,239,0.4)' },
          '&:active': { transform: 'translateY(0)' },
        },
        outlinedInherit: {
          border: '1.5px solid #E5E7EB',
          color: '#374151',
          backgroundColor: '#FFFFFF',
          '&:hover': { backgroundColor: '#F9FAFB', borderColor: '#D1D5DB' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: '0.72rem', height: 24 },
        colorError: { background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FECACA' },
        colorInfo: { background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' },
        colorSuccess: { background: '#DCFCE7', color: '#15803D', border: '1px solid #BBF7D0' },
        outlinedPrimary: { border: '1.5px solid #C7C9FF', background: '#ECEEFF', color: '#5B5FEF' },
        outlinedDefault: { border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#374151' },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: { color: '#D1D5DB', '&.Mui-checked': { color: '#5B5FEF' } },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: { background: '#DCFCE7', color: '#14532D', border: '1px solid #86EFAC', borderRadius: 12 },
        standardError: { background: '#FEE2E2', color: '#7F1D1D', border: '1px solid #FECACA', borderRadius: 12 },
        standardInfo: { background: '#ECEEFF', color: '#312E81', border: '1px solid #C7C9FF', borderRadius: 12 },
        standardWarning: { background: '#FEF3C7', color: '#78350F', border: '1px solid #FDE68A', borderRadius: 12 },
      },
    },
  },
})

// ── Helpers ────────────────────────────────────────────────────────────────────

const StatusDot = ({ color = '#16A34A' }) => (
  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0, boxShadow: `0 0 0 2px ${color}30` }} />
)

const SectionHeader = ({ icon, children, badge }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Typography sx={{ fontSize: 15 }}>{icon}</Typography>
    <Typography variant="subtitle2" sx={{ color: '#111827', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {children}
    </Typography>
    {badge && (
      <Box sx={{ ml: 'auto' }}>
        <Chip label={badge} size="small" color="primary" variant="outlined" />
      </Box>
    )}
  </Box>
)

const TypeCard = ({ value, selected, onChange, icon, title, subtitle, accent }) => (
  <Paper
    elevation={0}
    onClick={() => onChange(value)}
    sx={{
      flex: 1,
      p: 2,
      borderRadius: '12px',
      border: selected ? `2px solid ${accent || '#5B5FEF'}` : '2px solid #E5E7EB',
      bgcolor: selected ? `${accent ? accent + '12' : '#ECEEFF'}` : '#FAFAFA',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      transition: 'all 0.18s ease',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        borderColor: selected ? (accent || '#5B5FEF') : '#C7C9FF',
        bgcolor: selected ? `${accent ? accent + '12' : '#ECEEFF'}` : '#F5F6FF',
        transform: 'translateY(-1px)',
        boxShadow: selected ? `0 6px 20px ${accent ? accent + '25' : 'rgba(91,95,239,0.15)'}` : '0 4px 12px rgba(0,0,0,0.06)',
      },
      '&:active': { transform: 'translateY(0)' },
    }}
  >
    {selected && (
      <Box sx={{
        position: 'absolute', top: 0, right: 0, width: 32, height: 32,
        background: accent || '#5B5FEF', clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', pt: 0.5, pr: 0.5,
      }}>
        <Typography sx={{ fontSize: 9, color: '#fff', fontWeight: 800, lineHeight: 1 }}>✓</Typography>
      </Box>
    )}
    <Box sx={{
      width: 38, height: 38, borderRadius: 2,
      background: selected ? (accent || '#5B5FEF') : '#E5E7EB',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: selected ? '#fff' : '#6B7280', fontSize: 17, transition: 'all 0.18s',
    }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="subtitle2" sx={{ color: selected ? (accent || '#5B5FEF') : '#111827', fontSize: '0.875rem' }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', lineHeight: 1.4 }}>{subtitle}</Typography>
    </Box>
  </Paper>
)

const SummaryRow = ({ label, value, large, accent, icon }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
    <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem' }}>
      {label}
    </Typography>
    <Typography variant={large ? 'h4' : 'body1'} fontWeight={large ? 800 : 600}
      sx={{ color: accent ? '#5B5FEF' : '#111827', letterSpacing: large ? '-0.8px' : '-0.2px', lineHeight: 1.2 }}>
      {icon && <span style={{ marginRight: 4 }}>{icon}</span>}{value}
    </Typography>
  </Box>
)

const BomPdfCard = ({ url }) => {
  if (!url) return null
  return (
    <Paper elevation={0} sx={{
      p: 2, mb: 3, borderRadius: '12px',
      border: '2px solid #86EFAC', bgcolor: '#F0FDF4',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: '10px', bgcolor: '#16A34A',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, flexShrink: 0,
        }}>📄</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ color: '#14532D', fontWeight: 700 }}>Bill of Materials (PDF)</Typography>
          <Typography variant="caption" sx={{
            color: '#16A34A', display: 'block', overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: { xs: 160, sm: 300, md: 400 },
          }}>{url}</Typography>
        </Box>
      </Box>
      <Button variant="contained" size="small"
        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
        sx={{ bgcolor: '#16A34A', '&:hover': { bgcolor: '#15803D' }, gap: 0.75, px: 2.5, py: 1, borderRadius: '9px' }}
        startIcon={<span style={{ fontSize: 13 }}>↗</span>}
      >
        Open PDF
      </Button>
    </Paper>
  )
}

// ── AllocableCostDialog ────────────────────────────────────────────────────────
function AllocableCostDialog({ open, onClose, data, onDecision }) {
  if (!data || !data.nodes?.length) return null

  const [selections, setSelections] = useState(() => {
    const initial = {}
    data.nodes.forEach(node => {
      node.product_variants?.forEach(variant => {
        if (variant.is_allocable) initial[`${node.node_id}-${variant.id}`] = true
      })
    })
    return initial
  })

  const [globalSelection, setGlobalSelection] = useState('all')

  const allocableVariants = []
  data.nodes.forEach(node => {
    node.product_variants?.forEach(variant => {
      if (variant.is_allocable) allocableVariants.push({ nodeId: node.node_id, variantId: variant.id })
    })
  })

  const allSelected = allocableVariants.every(v => selections[`${v.nodeId}-${v.variantId}`] === true)
  const noneSelected = allocableVariants.every(v => selections[`${v.nodeId}-${v.variantId}`] === false)

  useEffect(() => {
    if (allSelected) setGlobalSelection('all')
    else if (noneSelected) setGlobalSelection('none')
    else setGlobalSelection('mixed')
  }, [selections])

  const handleGlobalChange = (e) => {
    const value = e.target.value
    setGlobalSelection(value)
    if (value === 'all' || value === 'none') {
      const include = value === 'all'
      setSelections(prev => {
        const next = { ...prev }
        allocableVariants.forEach(({ nodeId, variantId }) => { next[`${nodeId}-${variantId}`] = include })
        return next
      })
    }
  }

  const handleVariantChange = (nodeId, variantId) => (e) => {
    const include = e.target.value === 'yes'
    setSelections(prev => ({ ...prev, [`${nodeId}-${variantId}`]: include }))
  }

  const handleConfirm = () => {
    const payload = []
    data.nodes.forEach(node => {
      node.product_variants?.forEach(variant => {
        if (variant.is_allocable) {
          payload.push({
            node_id: Number(node.node_id),
            product_variant_id: Number(variant.id),
            is_cost: !!selections[`${node.node_id}-${variant.id}`],
          })
        }
      })
    })
    onDecision(payload)
   
  }

  const allocableCount = data.nodes.reduce((sum, node) => sum + (node.product_variants?.filter(v => v.is_allocable)?.length || 0), 0)
  const selectedCount = allocableVariants.filter(v => selections[`${v.nodeId}-${v.variantId}`]).length

  return (
    <ThemeProvider theme={dialogTheme}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2.5, borderBottom: '1px solid #E5E7EB', background: 'linear-gradient(135deg, #FAFAFA 0%, #F5F6FF 100%)' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0, boxShadow: '0 4px 12px rgba(217,119,6,0.3)' }}>💰</Box>
              <Box>
                <Typography variant="h6" sx={{ fontSize: '1rem', lineHeight: 1.3, color: '#111827' }}>Allocable Cost Configuration</Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                  {allocableCount} allocable item{allocableCount !== 1 ? 's' : ''} found &nbsp;·&nbsp;
                  <span style={{ color: '#5B5FEF', fontWeight: 600 }}>{selectedCount} selected</span>
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: '#9CA3AF', bgcolor: '#F3F4F6', borderRadius: '8px', '&:hover': { bgcolor: '#E5E7EB' } }}>
              <span style={{ fontSize: 13, lineHeight: 1 }}>✕</span>
            </IconButton>
          </Box>

          {/* Global toggle */}
          <Box sx={{ mt: 2.5, p: 2, borderRadius: '10px', bgcolor: '#fff', border: '1.5px solid #E5E7EB' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1 }}>
              Apply to all variants
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {['all', 'none'].map(val => (
                <Box key={val} onClick={() => handleGlobalChange({ target: { value: val } })}
                  sx={{
                    flex: 1, py: 1, px: 2, borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
                    border: '1.5px solid', transition: 'all 0.15s',
                    borderColor: globalSelection === val ? '#5B5FEF' : '#E5E7EB',
                    bgcolor: globalSelection === val ? '#ECEEFF' : '#FAFAFA',
                    '&:hover': { borderColor: '#C7C9FF', bgcolor: '#F5F6FF' },
                  }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: globalSelection === val ? '#5B5FEF' : '#6B7280', fontSize: '0.8rem' }}>
                    {val === 'all' ? '✓ Include All' : '✕ Exclude All'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, py: 2.5, maxHeight: '55vh', overflowY: 'auto' }}>
          {data.nodes.map((node) => (
            <Box key={node.node_id} sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5B5FEF', flexShrink: 0 }} />
                <Typography variant="subtitle2" sx={{ color: '#111827', fontWeight: 700, fontSize: '0.85rem' }}>
                  {node.name}
                </Typography>
                <Chip label={node.type} size="small" variant="outlined" sx={{ ml: 'auto', fontSize: '0.68rem', height: 20 }} />
              </Box>

              <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {node.product_variants?.map((pv) => (
                  <Box key={pv.id} sx={{
                    p: 1.5, borderRadius: '10px',
                    border: pv.is_allocable
                      ? (selections[`${node.node_id}-${pv.id}`] ? '1.5px solid #C7C9FF' : '1.5px solid #E5E7EB')
                      : '1px solid #F3F4F6',
                    bgcolor: pv.is_allocable
                      ? (selections[`${node.node_id}-${pv.id}`] ? '#F5F6FF' : '#FAFAFA')
                      : '#F9FAFB',
                    transition: 'all 0.15s',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#111827' }}>Variant #{pv.id}</Typography>
                          {pv.is_allocable && pv.cost && (
                            <Chip label={`₹ ${pv.cost?.toLocaleString()}`} size="small" color="success" sx={{ fontWeight: 700, height: 22, fontSize: '0.72rem' }} />
                          )}
                        </Box>
                        {pv.attributes && (
                          <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mt: 0.25 }}>
                            {Object.entries(pv.attributes).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                      {pv.is_allocable ? (
                        <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                          {[{ val: 'yes', label: 'Include', active: !!selections[`${node.node_id}-${pv.id}`] },
                            { val: 'no', label: 'Skip', active: !selections[`${node.node_id}-${pv.id}`] }].map(({ val, label, active }) => (
                            <Box key={val}
                              onClick={() => handleVariantChange(node.node_id, pv.id)({ target: { value: val } })}
                              sx={{
                                px: 1.5, py: 0.5, borderRadius: '7px', cursor: 'pointer',
                                border: '1.5px solid', transition: 'all 0.15s',
                                borderColor: active ? (val === 'yes' ? '#5B5FEF' : '#E5E7EB') : '#E5E7EB',
                                bgcolor: active ? (val === 'yes' ? '#ECEEFF' : '#F3F4F6') : '#FAFAFA',
                                '&:hover': { borderColor: val === 'yes' ? '#C7C9FF' : '#D1D5DB' },
                              }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: active ? (val === 'yes' ? '#5B5FEF' : '#374151') : '#9CA3AF', fontSize: '0.75rem' }}>
                                {val === 'yes' ? '✓' : '✕'} {label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Chip label="Non-allocable" size="small" variant="outlined" sx={{ opacity: 0.6, fontSize: '0.68rem', height: 22 }} />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #E5E7EB', display: 'flex', gap: 1.5, justifyContent: 'flex-end', bgcolor: '#FAFAFA' }}>
          <Button onClick={onClose} variant="outlined" color="inherit" sx={{ minWidth: 100 }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirm} sx={{ minWidth: 160 }}
            startIcon={<span style={{ fontSize: 14 }}>✓</span>}>
            Confirm · {selectedCount} selected
          </Button>
        </Box>
      </Dialog>
    </ThemeProvider>
  )
}

// ── Main CalculateDialog ───────────────────────────────────────────────────────
export default function CalculateDialog({
  CalculateDialogOpen, handleCloseCalculateDialog, workflowId,
  calcualtionneededfields, selectedmasterworkflow,
  handleUpdateNode, isUpdatingNodes, onUpdateAllNodes,setJobDialogOpen,
  variantIdFromUrl
}) {
  const {updatecalculationtype,updatecalculationquantity,updateisallocablecalculated}=useInventory()
  const [quantity, setQuantity] = useState('')
  const [attributeValues, setAttributeValues] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [results, setResults] = useState(null)
  const [variants, setVariants] = useState([])
  const [productVariants, setProductVariants] = useState([])
  const [finalOutputData, setFinalOutputData] = useState([])
  const [fetchingVariants, setFetchingVariants] = useState(false)
  const [productid, setproductid] = useState()
  const [aggregateData, setAggregateData] = useState([])
  const [aggregateValues, setAggregateValues] = useState({})
  const [calculationType, setcalculationType] = useState('Backtrack')
  const [allocableData, setAllocableData] = useState(null)
  const [showAllocableDialog, setShowAllocableDialog] = useState(false)
  const [allocableSelection, setAllocableSelection] = useState(null)
  const [fetchingAggregate, setFetchingAggregate] = useState(false)
  const [step, setStep] = useState(0) // 0=config, 1=calculating, 2=results

  React.useEffect(() => {
    if (calcualtionneededfields?.attributes) {
      const initialValues = {}
      calcualtionneededfields.attributes.forEach(attr => { initialValues[attr.attribute] = '' })
      setAttributeValues(initialValues)
    }
  }, [calcualtionneededfields])

  useEffect(()=>{
updatecalculationtype(calculationType)
updatecalculationquantity(quantity)
  },[calculationType,quantity])

  // Pre-select and display variant from URL query param (variant_id) if present
  useEffect(() => {
    const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
    const targetVariantId = variantIdFromUrl || searchParams?.get('variant_id')
    const productNameFromUrl = searchParams?.get('product_name')

    if (!targetVariantId || !CalculateDialogOpen) return

    let isMounted = true

    const syncTargetVariant = async () => {
      let variantObj = {
        id: Number(targetVariantId),
        attributes: {}
      }

      try {
        const variantsList = await fetcher(['/products-app/product-variant/list/'])
        if (Array.isArray(variantsList)) {
          const matched = variantsList.find(
            v => v.id === parseInt(targetVariantId, 10) || v.id?.toString() === targetVariantId.toString()
          )
          if (matched) {
            variantObj = matched
          }
        }
      } catch (e) {
        console.error('Error fetching variant details in CalculateDialog:', e)
      }

      if (!isMounted) return

      const displayName = variantObj.name || productNameFromUrl || `Variant #${targetVariantId}`
      
      setProductVariants([{
        label: displayName,
        value: targetVariantId.toString(),
        variant: { id: Number(targetVariantId), name: displayName }
      }])

      setFinalOutputData(prev => {
        if (!prev || prev.length === 0) return prev
        if (prev[0]?.product_variants?.[0]?.id === Number(targetVariantId)) {
          return prev
        }
        return prev.map(item => ({
          ...item,
          product_variants: [variantObj]
        }))
      })
    }

    syncTargetVariant()

    return () => { isMounted = false }
  }, [variantIdFromUrl, CalculateDialogOpen, finalOutputData])

  const fetchVariantsForProducts = async (productIds) => {
    if (!productIds || productIds.length === 0) { setVariants([]); return }
    setFetchingVariants(true)
    try {
      const variantResults = await Promise.all(productIds.map(id => fetcher([`/new/workflow-app/products/variants/list/${id}/`])))
      setVariants(variantResults.flat().map(item => ({ label: item.name, value: item.id.toString(), variant: { id: item.id, name: item.name } })))
    } catch (e) { setVariants([]) }
    finally { setFetchingVariants(false) }
  }

  const handlefetchaggregate = async () => {
    try {
      setFetchingAggregate(true)
      if (workflowId) {
        const res = await fetcher([`/new/workflow-app/workflow/outputs/aggregable-quantities/?workflow_creation_id=${workflowId}`])
        setAggregateData(res)
        const init = {}
        res.forEach(item => item.aggregable_keys.forEach(key => { init[`${item.product_id}.${key}`] = '' }))
        setAggregateValues(init)
      }
    } catch (e) { setError('Failed to fetch aggregate data') }
    finally { setFetchingAggregate(false) }
  }

  useEffect(() => { handlefetchaggregate() }, [workflowId, CalculateDialogOpen])

  const handleAggregateValueChange = (key, value) =>{
    updatecalculationquantity(value)
    setAggregateValues(prev => ({ ...prev, [key]: parseFloat(value) }))
    }

  const handlefetchfinal = async () => {
    if (selectedmasterworkflow) {
      try {
        const res = await fetcher([`/new/workflow-app/output/final/quantity/using/processcreation/id/?process_creation_id=${selectedmasterworkflow}`])
        setFinalOutputData(res)
        const noVariants = res.filter(o => o.product_variants?.length === 0)
        if (noVariants.length > 0) {
          const ids = noVariants.flatMap(o => o.products.map(p => p.id))
          fetchVariantsForProducts(ids)
          setproductid(ids)
        } else { setVariants([]) }
      } catch (e) { setFinalOutputData([]); setVariants([]) }
    }
  }

  useEffect(() => { handlefetchfinal() }, [selectedmasterworkflow, CalculateDialogOpen])

  const runMainCalculation = async (allocableArray) => {

    try {
      setLoading(true)
      updateisallocablecalculated(allocableArray)
      const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
      const targetVariantId = variantIdFromUrl || searchParams?.get('variant_id')
      const chosenVariantId = productVariants[0]?.variant?.id || (targetVariantId ? Number(targetVariantId) : undefined)

      const final_quantity = { product_variant_id: chosenVariantId }
      const payload = { workflow_creation_id: workflowId, final_quantity, is_allocable: allocableArray }
      if (calculationType === 'Backtrack') payload.backtrack_target = parseFloat(quantity)
      if (calculationType === 'Fronttrack') payload.aggregable_values = aggregateValues
      const endpoint = calculationType === 'Backtrack' ? '/new/workflow-app/backtrack/' : '/new/workflow-app/fronttrack/'
      const res = await postFetcher([endpoint, payload])
      if(res){
        setResults(res)
      }
    } catch (err) {
      setError(err.message || 'Calculation failed.')
    } finally { setLoading(false) }
  }

  const handleCalculate = async () => {
    if (calculationType === 'Fronttrack') {
      const missing = Object.keys(aggregateValues).filter(k => !aggregateValues[k])
      if (missing.length > 0) return setError(`Please fill: ${missing.join(', ')}`)
    }
    if (calculationType === 'Backtrack' && !quantity) return setError('Please enter a target quantity')

    setLoading(true); setError(''); setResults(null); setAllocableData(null); setShowAllocableDialog(false)
    try {
      const allocRes = await fetcher([`/new/workflow-app/cost/allocable/dialog/?workflow_creation_id=${workflowId}`])
      setAllocableData(allocRes)
      const hasAnyAllocable = allocRes?.nodes?.some(node => node.product_variants?.some(v => v.is_allocable === true)) ?? false
      if (hasAnyAllocable) { setShowAllocableDialog(true); return }
      await runMainCalculation([])
    } catch (err) {
      setError(err.message || 'Failed to prepare calculation.')
      setLoading(false)
    }
  }

  const handleClose = () => {
    setAttributeValues({}); setAggregateValues({}); setAggregateData({})
    setError(''); setResults(null); setFinalOutputData([]); setProductVariants([]); setVariants([])
    handleCloseCalculateDialog()
  }

  const isBacktrack = calculationType === 'Backtrack'
  const showVariantAutocomplete = variants.length > 0
  const hasResults = !!results

  const stepCount = results?.breakdown?.length || 0

  return (
    <ThemeProvider theme={dialogTheme}>
      <Dialog open={CalculateDialogOpen} onClose={loading ? undefined : handleClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column', maxHeight: '92vh' } }}>

        {/* ─── Header ─── */}
        <Box sx={{
          px: 3, pt: 3, pb: 2.5,
          background: hasResults
            ? 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)'
            : 'linear-gradient(135deg, #FAFAFA 0%, #F5F6FF 100%)',
          borderBottom: '1px solid',
          borderColor: hasResults ? '#86EFAC' : '#E5E7EB',
          transition: 'all 0.3s ease',
          flexShrink: 0,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '14px',
                background: hasResults
                  ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)'
                  : `linear-gradient(135deg, ${isBacktrack ? '#6366F1' : '#8B5CF6'} 0%, ${isBacktrack ? '#5B5FEF' : '#7C3AED'} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, flexShrink: 0,
                boxShadow: hasResults ? '0 6px 16px rgba(34,197,94,0.35)' : '0 6px 16px rgba(91,95,239,0.35)',
                transition: 'all 0.3s ease',
              }}>
                {hasResults ? '✅' : '⚙️'}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontSize: '1.05rem', color: '#111827', lineHeight: 1.3 }}>
                  {hasResults ? `${calculationType} Results` : `Calculate ${calculationType}`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <StatusDot color={hasResults ? '#16A34A' : loading ? '#D97706' : '#5B5FEF'} />
                  <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
                    {hasResults ? `${stepCount} steps computed` : loading ? 'Running calculation…' : 'Workflow quantity calculator'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Tooltip title="Close">
              <IconButton onClick={handleClose} disabled={loading} size="small"
                sx={{ color: '#9CA3AF', bgcolor: '#F3F4F6', borderRadius: '9px', '&:hover': { bgcolor: '#E5E7EB' } }}>
                <span style={{ fontSize: 14, lineHeight: 1, display: 'block', margin: '2px' }}>✕</span>
              </IconButton>
            </Tooltip>
          </Box>

          {/* Progress bar when loading */}
          {loading && (
            <Box sx={{ mt: 2, mx: -0.5 }}>
              <LinearProgress sx={{ borderRadius: 4, height: 3, bgcolor: '#E5E7EB', '& .MuiLinearProgress-bar': { bgcolor: '#5B5FEF' } }} />
            </Box>
          )}
        </Box>

        {/* ─── Content ─── */}
        <DialogContent sx={{ px: 3, py: 2.5, overflowY: 'auto', flex: 1 }}>
          {!results ? (
            <Box>
              {/* Error */}
              {error && (
                <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>{error}</Alert>
              )}

              {/* ── Calculation Type ── */}
              <Box sx={{ mb: 3 }}>
                <SectionHeader icon="⚡">Calculation Mode</SectionHeader>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TypeCard value="Backtrack" selected={isBacktrack} onChange={setcalculationType}
                    icon="←" title="Backtrack" subtitle="Start from target → compute raw materials needed" />
                  <TypeCard value="Fronttrack" selected={!isBacktrack} onChange={setcalculationType}
                    icon="→" title="Fronttrack" accent="#7C3AED"  subtitle="Start from raw inputs → compute final output" />
                </Box>
              </Box>

              <Divider sx={{ borderColor: '#F3F4F6', mb: 3 }} />

              {/* ── Output Data ── */}
              {finalOutputData.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <SectionHeader icon="📦" badge={`${finalOutputData.length} output${finalOutputData.length > 1 ? 's' : ''}`}>
                    Output Configuration
                  </SectionHeader>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {finalOutputData.map((output, index) => (
                      <Box key={index} sx={{ p: 2, borderRadius: '12px', border: '1.5px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <Box sx={{ width: 6, height: 20, borderRadius: 3, bgcolor: '#5B5FEF', flexShrink: 0 }} />
                          <Typography variant="subtitle2" sx={{ color: '#111827' }}>{output.output_name}</Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.75 }}>Products</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {output.products.map(p => <Chip key={p.id} label={`${p.name} · ${p.id}`} size="small" variant="outlined" />)}
                            </Box>
                          </Box>

                          {output.product_variants?.length > 0 && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.75 }}>Variants</Typography>
                              {output.product_variants.map(variant => (
                                <Box key={variant.id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  <Chip label={`ID: ${variant.id}`} size="small" variant="outlined" />
                                  {variant.attributes && Object.entries(variant.attributes).map(([k, v]) => (
                                    <Chip key={k} label={`${k}: ${v}`} size="small" color="primary" variant="outlined" />
                                  ))}
                                </Box>
                              ))}
                            </Box>
                          )}

                          <Box>
                            <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.75 }}>Categories</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {output.product_categories.map(cat => <Chip key={cat.id} label={`${cat.name} · ${cat.id}`} size="small" variant="outlined" />)}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {showVariantAutocomplete && (
                    <Box sx={{ mt: 1.5 }}>
                      <Autocomplete multiple options={variants} getOptionLabel={o => o.label}
                        value={productVariants} isOptionEqualToValue={(o, v) => o.value === v.value}
                        onChange={(_, val) => setProductVariants(val)}
                        renderInput={params => (
                          <TextField {...params} label="Select Variants" helperText="Select product variants to use in calculation"
                            InputProps={{ ...params.InputProps, endAdornment: (<>{fetchingVariants && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>) }} />
                        )}
                        fullWidth disabled={fetchingVariants}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: '10px', bgcolor: '#F9FAFB', border: '1.5px solid #E5E7EB' }}>
                  <CircularProgress size={16} sx={{ color: '#9CA3AF' }} />
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>Loading output configuration…</Typography>
                </Box>
              )}

              {/* ── Fronttrack: Aggregable Values ── */}
              {!isBacktrack && aggregateData.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <SectionHeader icon="🔢">Aggregable Input Values</SectionHeader>
                  <Box sx={{ p: 2, borderRadius: '12px', border: '1.5px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                    <Alert severity="info" sx={{ mb: 2, py: 0.75, fontSize: '0.8rem' }}>
                      Enter the raw input quantities to compute the final output.
                    </Alert>
                    {aggregateData.map((item, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                          <Typography variant="subtitle2" sx={{ fontSize: '0.82rem', color: '#374151' }}>{item.output}</Typography>
                          <Chip label={`Product #${item.product_id}`} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                          {item.aggregable_keys.map(key => {
                            const uniqueKey = `${item.product_id}.${key}`
                            return (
                              <TextField key={uniqueKey} type="number" label={key} placeholder="0"
                                InputProps={{ inputProps: { min: 0, step: 'any' } }}
                                value={aggregateValues[uniqueKey] ?? ''}
                                onChange={e => handleAggregateValueChange(uniqueKey, e.target.value)}
                                disabled={loading} sx={{ minWidth: 150, flex: 1 }} size="small" />
                            )
                          })}
                        </Box>
                        {index < aggregateData.length - 1 && <Divider sx={{ mt: 2, borderColor: '#F3F4F6' }} />}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* ── Backtrack: Target Quantity ── */}
              {isBacktrack && (
                <Box sx={{ mb: 1 }}>
                  <SectionHeader icon="🎯">Target Quantity</SectionHeader>
                  <TextField type="number" label="How many units do you want to produce?" fullWidth
                    placeholder="e.g. 500"
                    InputProps={{ inputProps: { min: 1, step: 1 }, startAdornment: <Box sx={{ mr: 1, color: '#9CA3AF', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Qty:</Box> }}
                    value={quantity}
                    onChange={e => { setQuantity(e.target.value); setError('') }}
                    onKeyPress={e => { if (e.key === 'Enter' && !loading) handleCalculate() }}
                    disabled={loading} error={!!error && isBacktrack}
                    helperText={!error ? 'Enter the number of units to produce — raw materials will be calculated automatically' : error}
                    autoFocus
                  />
                </Box>
              )}
            </Box>

          ) : (
            /* ─── RESULTS ─── */
            <Box>
              <Alert severity="success" sx={{ mb: 2.5 }}>
                {isBacktrack
                  ? `Backtrack complete — Target of ${quantity} units calculated across ${stepCount} steps`
                  : `Fronttrack complete — Final output: ${results.summary?.final_product?.final_computed_quantity || 'N/A'} pcs`}
              </Alert>

              <BomPdfCard url={results.bom_pdf_url} />

              {/* ── Summary Grid ── */}
              <Box sx={{ mb: 3 }}>
                <SectionHeader icon="📊">Summary</SectionHeader>
                <Box sx={{ p: 2.5, borderRadius: '14px', border: '2px solid #C7C9FF', bgcolor: '#F5F6FF' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                    <SummaryRow label="Workflow" value={results.summary?.workflow?.name || 'N/A'} />
                    <SummaryRow label="Final Output" value={`${results.summary?.final_product?.node_name || 'N/A'} → ${results.summary?.final_product?.output_name}`} />
                    <SummaryRow
                      label={isBacktrack ? 'Target Quantity' : 'Computed Output'}
                      value={`${isBacktrack ? quantity : results.summary?.final_product?.final_computed_quantity || 0} pcs`}
                      large accent
                    />
                    <SummaryRow label="Total Steps" value={`${results.summary?.total_steps || 0} nodes`} />
                  </Box>

                  {results.summary?.raw_material_requirements?.length > 0 && (
                    <Box sx={{ pt: 2, borderTop: '1.5px solid #C7C9FF' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.25 }}>
                        Raw Materials Required
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {results.summary.raw_material_requirements.map((mat, i) => (
                          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 1, borderRadius: '7px', bgcolor: 'rgba(255,255,255,0.7)' }}>
                            <Typography variant="body2" sx={{ color: '#374151' }}>
                              Product <strong>#{mat.product_id}</strong> → <span style={{ color: '#6B7280' }}>{mat.attribute_key}</span>
                            </Typography>
                            <Chip label={mat.required_value} size="small" color="success" sx={{ fontWeight: 700 }} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {results.summary?.raw_materials_used?.length > 0 && (
                    <Box sx={{ pt: 2, mt: 1, borderTop: '1.5px solid #C7C9FF' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.25 }}>
                        Raw Materials Used (Input)
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {results.summary.raw_materials_used.map((mat, i) => (
                          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 1, borderRadius: '7px', bgcolor: 'rgba(255,255,255,0.7)' }}>
                            <Typography variant="body2" sx={{ color: '#374151' }}>
                              Product <strong>#{mat.product_id}</strong> → <span style={{ color: '#6B7280' }}>{mat.attribute_key}</span>
                            </Typography>
                            <Chip label={mat.input_value} size="small" color="success" sx={{ fontWeight: 700 }} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* ── Step Breakdown ── */}
              <Box>
                <SectionHeader icon="🔍" badge={`${stepCount} steps`}>
                  Step-by-Step Breakdown
                </SectionHeader>
                <Box sx={{ borderRadius: '14px', border: '1.5px solid #E5E7EB', overflow: 'hidden' }}>
                  {results.breakdown?.map((step, index) => (
                    <Box key={step.output_id} sx={{
                      p: 2, borderBottom: index < results.breakdown.length - 1 ? '1px solid #F3F4F6' : 'none',
                      bgcolor: step.is_final_output ? '#F0FDF4' : index % 2 === 0 ? '#FFFFFF' : '#FAFAFA',
                      position: 'relative',
                      '&:hover': { bgcolor: step.is_final_output ? '#DCFCE7' : '#F5F6FF' },
                      transition: 'background 0.12s',
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box sx={{
                              width: 24, height: 24, borderRadius: '50%',
                              bgcolor: step.is_final_output ? '#DCFCE7' : '#F3F4F6',
                              border: `1.5px solid ${step.is_final_output ? '#86EFAC' : '#E5E7EB'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.68rem', fontWeight: 800,
                              color: step.is_final_output ? '#16A34A' : '#6B7280',
                              flexShrink: 0,
                            }}>
                              {index + 1}
                            </Box>
                            <Typography variant="subtitle2" sx={{ color: '#111827', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {step.node?.name || 'Unnamed Node'}
                            </Typography>
                            {step.is_final_output && (
                              <Chip label="FINAL" color="success" size="small" sx={{ fontSize: '0.65rem', height: 18, fontWeight: 800, flexShrink: 0 }} />
                            )}
                          </Box>

                          <Box sx={{ ml: 4 }}>
                            <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block' }}>
                              {step.output_name} <span style={{ color: '#D1D5DB' }}>·</span> ID: {step.output_id}
                            </Typography>

                            {step.formula && step.formula !== 'Pass-through' && step.formula !== 'Pass-through (auto-resolved)' && (
                              <Box sx={{
                                mt: 0.75, px: 1.25, py: 0.4, borderRadius: '6px',
                                bgcolor: '#F0F0FF', border: '1px solid #C7C9FF',
                                display: 'inline-block', fontFamily: 'monospace', fontSize: '0.75rem', color: '#5B5FEF',
                              }}>
                                {typeof step.formula === 'object' ? JSON.stringify(step.formula) : step.formula}
                              </Box>
                            )}

                            {step.node?.inputs?.length > 0 && (
                              <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {step.node.inputs.map((inp, i) => (
                                  <Chip key={i} label={inp.name} size="small"
                                    color={inp.is_material ? 'error' : 'info'} variant="outlined" />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography variant="h5" fontWeight={800} sx={{
                            color: step.is_final_output ? '#16A34A' : '#5B5FEF',
                            fontSize: '1.35rem', letterSpacing: '-0.5px', lineHeight: 1,
                          }}>
                            {isBacktrack ? step.required_quantity : step.computed_quantity}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 500 }}>
                            {step.unit || 'pcs'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        {/* ─── Actions ─── */}
        <Box sx={{ px: 3, py: 2.5, borderTop: '1px solid #E5E7EB', display: 'flex', gap: 1.5, justifyContent: 'space-between', alignItems: 'center', bgcolor: '#FAFAFA', flexShrink: 0 }}>
          {!results ? (
            <>
              <Button onClick={handleClose} disabled={loading} variant="outlined" color="inherit" sx={{ minWidth: 100 }}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleCalculate} disabled={loading}
                startIcon={loading ? <CircularProgress size={15} color="inherit" thickness={5} /> : <span style={{ fontSize: 14 }}>⚡</span>}
                sx={{ minWidth: 160 }}>
                {loading ? 'Calculating…' : `Run ${calculationType}`}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => { setResults(null); setQuantity(''); setAttributeValues({}) }}
                variant="outlined" color="inherit" startIcon={<span style={{ fontSize: 13 }}>←</span>}
                sx={{ minWidth: 130 }}>
                New Calculation
              </Button>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button variant="outlined" color="inherit"
                  onClick={() => onUpdateAllNodes(results)}
                  disabled={loading || isUpdatingNodes}
                  startIcon={isUpdatingNodes ? <CircularProgress size={14} color="inherit" /> : <Iconify icon="eva:save-fill" />}
                  sx={{ px: 2.5 }}>
                  {isUpdatingNodes ? 'Updating…' : 'Save to Nodes'}
                </Button>
                <Button variant="contained" color="primary" onClick={handleClose} startIcon={<span style={{ fontSize: 13 }}>✓</span>} sx={{ minWidth: 90 }}>
                  Done
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      <AllocableCostDialog
        open={showAllocableDialog}
        onClose={() => { setShowAllocableDialog(false); setLoading(false); }}
        data={allocableData}
        onDecision={(allocableArray) => { setShowAllocableDialog(false); runMainCalculation(allocableArray); }}
      />
    </ThemeProvider>
  )
}